import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';
import { FETCH_UTILITIES } from '~/components/dropdowns/utility/utility.component';
import { fetchLmiZoneLookup } from '~/services/fetch-lmi-zone-lookup';
import { AddressType } from '~/typings/context';
import { trackError } from '~/utils/tracking';
import { copy } from './copy';
import { parseAddress } from './parse-address';

type ValidationProps = {
  state?: {
    abbreviation: string;
    name: string;
  };
  utilityId?: number;
  // When both are set, the hook checks LMI eligibility immediately after the
  // address is validated. The address field is marked invalid and the
  // submit button stays disabled when the address is not LMI-eligible.
  shouldValidateLMIAddress?: boolean;
  utilityAccountId?: number;
};

export type ValidationResult = {
  isValid: boolean;
  error?: string;
};

type ValidatableAddress = AddressType | ReturnType<typeof parseAddress>;

export const useAddressValidation = ({
  state,
  utilityId,
  shouldValidateLMIAddress,
  utilityAccountId,
}: ValidationProps) => {
  const apolloClient = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);

  const validateAddress = useCallback(
    async (addressToValidate: ValidatableAddress): Promise<ValidationResult> => {
      setIsLoading(true);

      const incomplete =
        !addressToValidate.state ||
        !addressToValidate.city ||
        !addressToValidate.street ||
        !addressToValidate.zipCode;

      if (incomplete) {
        setIsLoading(false);
        return { isValid: false, error: copy.errors.invalidAddress };
      }

      // 2. State check
      if (state && addressToValidate.state !== state.abbreviation) {
        setIsLoading(false);
        return {
          isValid: false,
          error: copy.errors.invalidAddressForState(state.name),
        };
      }

      // 3. Utility check
      if (utilityId) {
        try {
          const { data } = await apolloClient.query({
            query: FETCH_UTILITIES,
            variables: { zipCode: addressToValidate.zipCode },
          });

          const isValidUtility = data.utilities?.suggested?.find(
            (utility: any) => utility?.id === utilityId,
          );

          if (!isValidUtility) {
            setIsLoading(false);
            return {
              isValid: false,
              error: copy.errors.utilityServiceAreaMismatch,
            };
          }
        } catch (error) {
          trackError(new Error('Error validating address'), { extras: { error } });
          setIsLoading(false);
          return {
            isValid: false,
            error: copy.errors.addressValidationFailed,
          };
        }
      }

      // 4. LMI eligibility check — runs with the freshly-typed address so the
      // user gets instant feedback before the form is submitted. Requires a
      // utilityAccountId (the UA is always created before the address step).
      if (shouldValidateLMIAddress && utilityAccountId) {
        try {
          const lmiResult = await fetchLmiZoneLookup({
            utilityAccountId,
            street: addressToValidate.street,
            city: addressToValidate.city,
            state: addressToValidate.state,
            zipCode: addressToValidate.zipCode,
          });

          if (lmiResult.error) {
            throw new Error('LMI zone lookup returned an error');
          }

          if (!lmiResult.isInLmiArea && !lmiResult.isInCensusTract) {
            setIsLoading(false);
            return {
              isValid: false,
              error: copy.errors.lmiNotEligible,
            };
          }
        } catch (error) {
          trackError(new Error('Error checking LMI eligibility'), {
            extras: { error, addressToValidate },
          });
          setIsLoading(false);
          return {
            isValid: false,
            error: copy.errors.lmiCheckFailed,
          };
        }
      }

      // 5. Passed all checks
      setIsLoading(false);
      return { isValid: true, error: undefined };
    },
    [apolloClient, state, utilityId, shouldValidateLMIAddress, utilityAccountId],
  );

  return { validateAddress, isLoading };
};
